import type { ITenantDestination } from '@/entities/tenant-destination'
import type { ITourPoi, TourBasicInfoState } from '@/shared/api/types/tour-types'
import { useCallback, useEffect, useMemo } from 'react'
import { useCountriesQuery, useProvincesQuery } from '@/entities/country'
import {
  useTenantDestinationByIdQuery,
  useTenantDestinationsQuery,
} from '@/entities/tenant-destination'
import {
  findDestinationGeo,
  inferGeoFromSelectedPois,
  syncBasicInfoWithDestination,
} from '@/features/tour/tour-form-model'
import { DEFAULT_FETCH_LIMIT } from '@/shared/constants/pagination'

interface UseTourFormOptionsParams {
  basicInfo: TourBasicInfoState
  selectedPois: ITourPoi[]
  /** Language the destination names are shown in (the POI picker language). */
  languageCode: string
  updateBasicInfo: (updater: (prev: TourBasicInfoState) => TourBasicInfoState) => void
}

/** Country, province and tenant-destination choices for the basic-info section, and their cross-field syncing. */
export function useTourFormOptions({
  basicInfo,
  selectedPois,
  languageCode,
  updateBasicInfo,
}: UseTourFormOptionsParams) {
  const { data: countriesResponse } = useCountriesQuery({})
  const { data: provincesResponse, isLoading: isProvincesLoading } = useProvincesQuery({
    countryId: basicInfo.countryId,
  })
  const { data: tenantDestinationsResponse, isLoading: isTenantDestinationsLoading } =
    useTenantDestinationsQuery({
      current: 1,
      pageSize: DEFAULT_FETCH_LIMIT,
      isActive: true,
      ...(basicInfo.countryId ? { countryId: basicInfo.countryId } : {}),
      ...(basicInfo.provinceId ? { provinceId: basicInfo.provinceId } : {}),
      ...(languageCode ? { languageCode } : {}),
    })

  const tenantDestinationItems = useMemo<ITenantDestination[]>(
    () => tenantDestinationsResponse?.data?.data ?? [],
    [tenantDestinationsResponse?.data?.data],
  )
  const selectedFromList = useMemo(
    () => tenantDestinationItems.find((item) => item.id === basicInfo.tenantDestinationId),
    [basicInfo.tenantDestinationId, tenantDestinationItems],
  )
  const { data: selectedDestinationResponse } = useTenantDestinationByIdQuery(
    basicInfo.tenantDestinationId,
    languageCode || undefined,
    { enabled: !!languageCode },
  )
  const selectedTenantDestination = selectedDestinationResponse?.data ?? selectedFromList

  // * VI: Điểm đến đang chọn có thể nằm ngoài trang danh sách đã tải (đang lọc theo tỉnh/quốc gia khác); thêm nó vào đầu danh sách để Select vẫn hiển thị đúng. | EN: The selected destination may fall outside the loaded list (another province/country filter is active); prepend it so the Select still renders it.
  const resolvedTenantDestinationItems = useMemo(() => {
    if (!selectedTenantDestination) {
      return tenantDestinationItems
    }
    return tenantDestinationItems.some((item) => item.id === selectedTenantDestination.id)
      ? tenantDestinationItems
      : [selectedTenantDestination, ...tenantDestinationItems]
  }, [selectedTenantDestination, tenantDestinationItems])

  const countryOptions = useMemo(
    () =>
      (countriesResponse?.data ?? []).map((item) => ({
        value: item.id,
        label: item.nativeName ?? item.name ?? item.code,
      })),
    [countriesResponse],
  )
  const provinceOptions = useMemo(
    () =>
      (provincesResponse?.data ?? []).map((item) => ({
        value: item.id,
        label: item.nativeName ?? item.name ?? item.code,
      })),
    [provincesResponse],
  )
  const tenantDestinationOptions = useMemo(
    () =>
      resolvedTenantDestinationItems.map((item) => ({
        value: item.id,
        label: item.localizedData?.[languageCode]?.name ?? 'No content available',
      })),
    [languageCode, resolvedTenantDestinationItems],
  )

  // * VI: Khi chọn điểm đến, tự đồng bộ country/province theo điểm đến đó để bộ lọc địa lý không bị lệch. | EN: When a destination is selected, sync country/province from it so the geographic filters stay consistent.
  useEffect(() => {
    if (!basicInfo.tenantDestinationId) {
      return
    }
    const geo = findDestinationGeo(basicInfo.tenantDestinationId, resolvedTenantDestinationItems)
    if (
      !geo ||
      (basicInfo.countryId === geo.countryId && basicInfo.provinceId === geo.provinceId)
    ) {
      return
    }
    updateBasicInfo((prev) => ({ ...prev, ...geo }))
  }, [
    basicInfo.countryId,
    basicInfo.provinceId,
    basicInfo.tenantDestinationId,
    resolvedTenantDestinationItems,
    updateBasicInfo,
  ])

  // * VI: Chưa có tỉnh nhưng đã có POI: suy country/province chung từ các POI để giảm thao tác nhập tay. | EN: No province yet but POIs are selected: infer the common country/province from them to reduce manual input.
  useEffect(() => {
    if (
      basicInfo.provinceId ||
      selectedPois.length === 0 ||
      resolvedTenantDestinationItems.length === 0
    ) {
      return
    }
    const geo = inferGeoFromSelectedPois(selectedPois, resolvedTenantDestinationItems)
    if (geo) {
      updateBasicInfo((prev) => ({ ...prev, ...geo }))
    }
  }, [
    basicInfo.provinceId,
    basicInfo.scopeType,
    selectedPois,
    resolvedTenantDestinationItems,
    updateBasicInfo,
  ])

  const handleBasicInfoChange = useCallback(
    (value: TourBasicInfoState) =>
      updateBasicInfo(() => syncBasicInfoWithDestination(value, resolvedTenantDestinationItems)),
    [resolvedTenantDestinationItems, updateBasicInfo],
  )

  return {
    countryOptions,
    provinceOptions,
    tenantDestinationOptions,
    isProvincesLoading,
    isTenantDestinationsLoading,
    selectedTenantDestination,
    handleBasicInfoChange,
  }
}
